// src/utils/pdfUtils.js
// FIXED CODE - Moved Total Sheets/Time to a separate table

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Attempts to get an address field, trying multiple key variations.
 * Handles plain strings or DynamoDB AttributeValue objects like { S: "..." }.
 * @param {object} dataObj The object containing the field (e.g., row, row.billingAddress.M).
 * @param {string[]} keysToCheck An array of keys to try in order (e.g., ['Contact', 'name']).
 * @returns {string} The value found, or an empty string.
 */
function getAddressField(dataObj, keysToCheck) {
    if (!dataObj || typeof dataObj !== 'object') {
        return '';
    }
    for (const key of keysToCheck) {
        if (dataObj[key] !== undefined && dataObj[key] !== null) {
            const value = dataObj[key];
            // Check for DynamoDB AttributeValue map first
            if (typeof value === 'object' && value !== null) {
                if ('S' in value) return value.S || '';
                if ('N' in value) return String(value.N) || ''; // Convert number to string
                if ('BOOL' in value) return String(value.BOOL); // Convert boolean to string
                // Add other DynamoDB types like L, M if necessary, though unlikely for simple fields
            }
            // Handle plain values (if dataObj is already flattened)
            if (typeof value === 'string') return value;
            if (typeof value === 'number') return String(value);
            if (typeof value === 'boolean') return String(value);
        }
    }
    // Special case: Combine first/last name if 'name' or 'Contact' not found
    if (keysToCheck.includes('Contact') || keysToCheck.includes('name')) {
        const firstName = getAddressField(dataObj, ['first_name', 'firstName']);
        const lastName = getAddressField(dataObj, ['last_name', 'lastName']);
        if (firstName || lastName) {
            return `${firstName} ${lastName}`.trim();
        }
    }
    return ''; // Not found
}

/** Convert decimal hours (e.g. 1.37) to "H:MM" */
function decimalHoursToHM(decimalValue) {
    const numValue = parseFloat(decimalValue); // Ensure it's a number
    if (isNaN(numValue) || numValue === null) return '';
    const hours = Math.floor(numValue);
    const fractional = numValue - hours;
    const minutes = Math.round(fractional * 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Generates a Job Sheet PDF for a single line item (the `row` object).
 * The `row` object is expected to be a flattened representation from pages.
 * Handles different address key formats and displays GT Narration and Notes.
 * @param {object} row The row data.
 * @param {'download' | 'print'} outputType How to output the PDF.
 */
export function generateJobSheetPDF(row, outputType = 'download') {
    if (!row) {
        console.error("generateJobSheetPDF called with invalid row data:", row);
        alert("Cannot generate PDF: Invalid order data.");
        return;
    }
    const doc = new jsPDF();

    // --- Date, Order ID, Price ---
    const dateStr = row.orderDate
        ? new Date(row.orderDate).toLocaleDateString('en-NZ')
        : 'N/A';
    const lineSuffix = row._lineIndex !== undefined ? `-${row._lineIndex + 1}` : '';
    const orderIdValue = row.orderId || 'UNKNOWN_ID';
    // Remove prefix for display if it exists (like PO#)
    const prefixMatch = orderIdValue.match(/^(.*?#)?(.*)$/); // Match optional prefix ending in #
    const displayIdWithoutPrefix = prefixMatch ? prefixMatch[2] : orderIdValue; // Get the part after #, or the whole thing
    const displayedId = `${orderIdValue.startsWith('PO#') ? 'PO#' : ''}${displayIdWithoutPrefix}${lineSuffix}`; // Re-add PO# prefix if needed


    doc.setFontSize(16);
    doc.text(`Date: ${dateStr}`, 105, 15, { align: 'center' });
    doc.text(`Order ID: ${displayedId}`, 105, 23, { align: 'center' });

    const linePrice = parseFloat(row.price) || 0;
    const lineQty = parseFloat(row.quantity) || 0;
    const lineTotalPrice = (linePrice * lineQty).toFixed(2);

    doc.text(`Total Price: $${lineTotalPrice}`, 105, 31, { align: 'center' });
    doc.setFontSize(12);

    let yPos = 40; // Initial Y position below the header

    // --- Prepare data for all tables ---
    const boardGrade = getAddressField(row, ['Board_Grade']);
    const boxStyle = getAddressField(row, ['Box_Style']);
    const qty = lineQty;
    const totalLength = getAddressField(row, ['_totalLength']);
    const totalWidth = getAddressField(row, ['_totalWidth']);
    const totalSheets = getAddressField(row, ['_totalSheets']);
    const rawTime = getAddressField(row, ['_totalTime']);
    const totalTime = decimalHoursToHM(rawTime);
    const lengthVal = getAddressField(row, ['length', 'Length']);
    const widthVal = getAddressField(row, ['width', 'Width']);
    const heightVal = getAddressField(row, ['height', 'Height']);
    const gtNarration = getAddressField(row, ['notes']); // Existing notes field
    const adminNotes = getAddressField(row, ['adminNotes']); // New field

    // --- Table 1: Core Item Details (Sheets/Time Removed) ---
    autoTable(doc, {
        startY: yPos,
        head: [[ // Header updated
            'Board Grade', 'Box Style', 'Qty',
            '(L) totalLength', '(W) totalWidth', // Removed Total Sheets, Total Time
        ]],
        body: [[ // Body updated
            boardGrade, boxStyle, qty,
            totalLength, totalWidth, // Removed totalSheets, totalTime
        ]],
        margin: { left: 14, right: 14 },
        theme: 'striped',
        headStyles: { fillColor: [22, 160, 133], fontSize: 10 },
        bodyStyles: { fontSize: 10 },
    });
    yPos = doc.lastAutoTable.finalY + 10; // Update yPos

    // --- NEW Table 2: Sheets and Time ---
    autoTable(doc, {
        startY: yPos, // Position after Table 1
        head: [['Total Sheets', 'Total Time']],
        body: [[totalSheets, totalTime]],
        margin: { left: 14, right: 14 },
        theme: 'grid', // Use grid theme for this table
        headStyles: { fontSize: 10 },
        bodyStyles: { fontSize: 10 },
    });
    yPos = doc.lastAutoTable.finalY + 10; // Update yPos again

    // --- Table 3: Dimensions (L, W, H) ---
    autoTable(doc, {
        startY: yPos, // Position after NEW Table 2
        head: [['Length (L)', 'Width (W)', 'Height (H)']],
        body: [[lengthVal, widthVal, heightVal]],
        margin: { left: 14, right: 14 },
        theme: 'striped', // Back to striped theme
        headStyles: { fillColor: [22, 160, 133], fontSize: 10 },
        bodyStyles: { fontSize: 10 },
    });
    yPos = doc.lastAutoTable.finalY + 10; // Update yPos

    // --- GT Narration ---
    doc.setFontSize(11);
    doc.text('GT Narration:', 14, yPos);
    yPos += 6;
    doc.setFontSize(10);
    if (gtNarration && gtNarration.trim()) {
        const narrationLines = doc.splitTextToSize(gtNarration, 180);
        doc.text(narrationLines, 14, yPos);
        yPos += narrationLines.length * 5 + 3;
    } else {
        doc.text('None', 14, yPos);
        yPos += 7;
    }
    yPos += 3;

    // --- Notes (New Editable Field) ---
    doc.setFontSize(11);
    doc.text('Notes:', 14, yPos);
    yPos += 6;
    doc.setFontSize(10);
    if (adminNotes && adminNotes.trim()) {
        const notesLines = doc.splitTextToSize(adminNotes, 180);
        doc.text(notesLines, 14, yPos);
        yPos += notesLines.length * 5 + 3;
    } else {
        doc.text('None', 14, yPos);
        yPos += 7;
    }
    yPos += 5;

    // --- Delivery Address ---
    let addressSource = {};
    if (row.shippingAddress && Object.keys(row.shippingAddress).length > 0) {
        addressSource = row.shippingAddress.M || row.shippingAddress;
    } else if (row.billingAddress && Object.keys(row.billingAddress).length > 0) {
        addressSource = row.billingAddress.M || row.billingAddress;
    }

    doc.setFontSize(11);
    doc.text('Delivery Address:', 14, yPos);
    yPos += 7;
    doc.setFontSize(10);

    const addressLines = [
        getAddressField(addressSource, ['Contact', 'name', 'first_name']),
        getAddressField(addressSource, ['company', 'Company']),
        getAddressField(addressSource, ['Address1', 'address1']),
        getAddressField(addressSource, ['Address2', 'address2']),
        [
            getAddressField(addressSource, ['Suburb', 'city']),
            getAddressField(addressSource, ['State', 'province']),
            getAddressField(addressSource, ['Postcode', 'zip'])
        ].filter(Boolean).join(', '),
        getAddressField(addressSource, ['Country', 'country']),
        getAddressField(addressSource, ['phone', 'Phone']) ? `Phone: ${getAddressField(addressSource, ['phone', 'Phone'])}` : ''
    ].filter(line => line && line.trim() !== '');

    if (addressLines.length > 0) {
        addressLines.forEach(line => {
            const splitLines = doc.splitTextToSize(line, 180);
            doc.text(splitLines, 14, yPos);
            yPos += splitLines.length * 5;
        });
    } else {
        doc.text('Address not provided.', 14, yPos);
        yPos += 7;
    }

    // --- Final Save ---
    const safeOrderId = displayedId.replace(/[^a-zA-Z0-9_-]/g, '_');
    // doc.save(`JobSheet_${safeOrderId}.pdf`);

    if (outputType === 'print') {
        doc.autoPrint();
        doc.output('dataurlnewwindow'); // Opens in new tab and triggers print dialog
    } else { // Default to download
        doc.save(`JobSheet_${safeOrderId}.pdf`);
    }
}